import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Mail, Lock, User as UserIcon, Hash, ArrowRight, KeyRound,
  GraduationCap, Cross, ChevronLeft, ShieldCheck, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { ESCUDO, formatApiError, api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const INST_DOMAIN = "@virreysolis.edu.co";

function isInstitutional(email) {
  return typeof email === "string" && email.toLowerCase().endsWith(INST_DOMAIN);
}

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const target = location.state?.from?.pathname;
  const [role, setRole] = useState(null); // null | "catechist" | "student"

  const goDashboard = (user) => {
    const isStaff = user.role === "catechist" || user.role === "admin";
    const dest = target || (isStaff ? "/dashboard/catequista" : "/dashboard/estudiante");
    navigate(dest, { replace: true });
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] flex" data-testid="login-page">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 franciscan-pattern opacity-20" />
        <Link to="/" className="relative inline-flex items-center gap-3" data-testid="brand-link">
          <span className="inline-flex w-16 h-16 rounded-full bg-white p-1.5">
            <img src={ESCUDO} alt="Escudo" className="w-full h-full object-contain rounded-full" />
          </span>
          <div className="leading-tight">
            <div className="font-display text-2xl font-bold">Pastoral Virreyista</div>
            <div className="text-xs tracking-[0.3em] uppercase text-secondary font-bold">Virtud & Ciencia · 2026</div>
          </div>
        </Link>
        <div className="relative animate-fade-up">
          <p className="text-xs tracking-[0.3em] uppercase text-secondary font-bold mb-4 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" /> Acceso institucional
          </p>
          <h2 className="font-display text-5xl xl:text-6xl leading-[1.05] mb-6">
            Solo familias<br />y catequistas<br /><em className="text-secondary">virreyistas</em>.
          </h2>
          <p className="text-lg opacity-90 max-w-md">
            El contenido catequético está reservado para los miembros del Colegio Franciscano del Virrey Solís.
            Ingresa con tu correo <strong className="text-secondary">{INST_DOMAIN}</strong>.
          </p>
        </div>
        <div className="relative text-sm opacity-70">Pax et Bonum · Bogotá</div>
      </div>

      {/* Right side */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <img src={ESCUDO} alt="Escudo" className="w-14 h-14 rounded-full bg-white p-1 border-2 border-primary" />
            <div>
              <div className="font-display text-xl text-primary font-bold">Pastoral Virreyista</div>
              <div className="text-xs tracking-[0.2em] uppercase text-secondary font-bold">2026</div>
            </div>
          </div>

          {!role && <RoleSelector onSelect={setRole} />}
          {role === "catechist" && (
            <CatechistAuth onBack={() => setRole(null)} onSuccess={goDashboard} login={login} />
          )}
          {role === "student" && (
            <StudentAuth onBack={() => setRole(null)} onSuccess={goDashboard} login={login} register={register} />
          )}

          <p className="text-xs text-center mt-8 text-foreground/60">
            <Link to="/" className="hover:text-primary">← Volver al inicio público</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function RoleSelector({ onSelect }) {
  return (
    <div className="animate-fade-up" data-testid="role-selector">
      <h1 className="font-display text-4xl lg:text-5xl text-primary leading-tight">¿Cómo ingresas hoy?</h1>
      <p className="text-sm text-foreground/70 mt-2 mb-8">Elige tu acceso para continuar.</p>

      <button
        onClick={() => onSelect("catechist")}
        data-testid="role-catechist"
        className="w-full group mb-4 text-left rounded-2xl border-2 border-primary/15 bg-card hover:border-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 p-6 hover:-translate-y-0.5 hover:shadow-lg"
      >
        <div className="flex items-center gap-5">
          <span className="shrink-0 w-14 h-14 rounded-2xl bg-primary text-primary-foreground group-hover:bg-secondary group-hover:text-secondary-foreground flex items-center justify-center transition-colors">
            <Cross className="w-7 h-7" />
          </span>
          <div className="flex-1">
            <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-secondary mb-1">Acceso pastoral</p>
            <p className="font-display text-2xl">SOY CATEQUISTA</p>
            <p className="text-xs opacity-70 mt-1">Gestiono contenidos, califico tareas y acompaño grupos.</p>
          </div>
          <ArrowRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </button>

      <button
        onClick={() => onSelect("student")}
        data-testid="role-student"
        className="w-full group text-left rounded-2xl border-2 border-secondary/30 bg-card hover:border-secondary hover:bg-secondary hover:text-secondary-foreground transition-all duration-300 p-6 hover:-translate-y-0.5 hover:shadow-lg"
      >
        <div className="flex items-center gap-5">
          <span className="shrink-0 w-14 h-14 rounded-2xl bg-secondary text-secondary-foreground group-hover:bg-primary group-hover:text-primary-foreground flex items-center justify-center transition-colors">
            <GraduationCap className="w-7 h-7" />
          </span>
          <div className="flex-1">
            <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-primary mb-1">Familia virreyista</p>
            <p className="font-display text-2xl">SOY ESTUDIANTE</p>
            <p className="text-xs opacity-70 mt-1">Veo mis temas, hago actividades y subo tareas.</p>
          </div>
          <ArrowRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </button>

      <p className="mt-8 text-xs text-foreground/60 flex items-center gap-2 justify-center">
        <ShieldCheck className="w-3.5 h-3.5 text-primary" />
        Acceso exclusivo con correo <code className="bg-muted px-1 rounded font-mono">{INST_DOMAIN}</code>
      </p>
    </div>
  );
}

function CatechistAuth({ onBack, onSuccess, login }) {
  const [form, setForm] = useState({ email: "", password: "", remember: true });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isInstitutional(form.email)) {
      setError(`Usa tu correo institucional ${INST_DOMAIN}`);
      return;
    }
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      if (user.role !== "catechist" && user.role !== "admin") {
        setError("Esta cuenta no es de catequista. Vuelve y elige 'Soy estudiante'.");
        return;
      }
      toast.success(`¡Bienvenido/a, ${user.name}!`);
      onSuccess(user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "No se pudo iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-fade-up" data-testid="catechist-auth">
      <button onClick={onBack} className="text-sm text-foreground/70 hover:text-primary mb-4 inline-flex items-center gap-1" data-testid="back-roles">
        <ChevronLeft className="w-4 h-4" /> Volver
      </button>
      <p className="text-xs tracking-[0.3em] uppercase font-bold text-secondary mb-2 flex items-center gap-2">
        <Cross className="w-3.5 h-3.5" /> Acceso pastoral
      </p>
      <h1 className="font-display text-4xl text-primary mb-2">Soy catequista</h1>
      <p className="text-sm text-foreground/70 mb-7">Ingresa con tu correo institucional.</p>

      <form onSubmit={submit} className="space-y-4" data-testid="catechist-form">
        <div>
          <Label htmlFor="cat-email" className="text-xs tracking-wider uppercase font-semibold">Correo institucional</Label>
          <div className="relative mt-1.5">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/50" />
            <Input
              id="cat-email" type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="pl-9 h-11" placeholder={`tu_nombre${INST_DOMAIN}`}
              data-testid="cat-email"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="cat-password" className="text-xs tracking-wider uppercase font-semibold">Contraseña</Label>
          <div className="relative mt-1.5">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/50" />
            <Input
              id="cat-password" type="password" required value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="pl-9 h-11" placeholder="••••••••"
              data-testid="cat-password"
            />
          </div>
        </div>
        <div className="flex items-center justify-between text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={form.remember} onCheckedChange={(v) => setForm({ ...form, remember: !!v })} />
            <span className="text-foreground/70">Recordarme</span>
          </label>
          <button
            type="button"
            onClick={() => setForgotOpen(true)}
            className="text-primary hover:underline"
            data-testid="cat-forgot"
          >
            ¿Olvidaste tu contraseña?
          </button>
        </div>
        {error && <p className="text-sm text-destructive font-semibold" data-testid="cat-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full h-11 rounded-full font-semibold" data-testid="cat-submit">
          {loading ? "Ingresando..." : "Ingresar como catequista"} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </form>

      <ForgotPasswordDialog open={forgotOpen} onClose={() => setForgotOpen(false)} defaultEmail={form.email} />

      <p className="mt-6 text-xs text-foreground/60 text-center">
        Las cuentas de catequista las gestiona la coordinación pastoral.<br />
        Solicítala con tu correo institucional <code className="bg-muted px-1 rounded">{INST_DOMAIN}</code>.
      </p>
    </div>
  );
}

function StudentAuth({ onBack, onSuccess, login, register }) {
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: "", password: "", remember: true });
  const [regForm, setRegForm] = useState({ name: "", email: "", password: "", group_code: "" });

  const submitLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(loginForm.email, loginForm.password);
      toast.success(`¡Paz y bien, ${user.name}!`);
      onSuccess(user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "No se pudo iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await register({
        name: regForm.name,
        email: regForm.email,
        password: regForm.password,
        group_code: regForm.group_code ? regForm.group_code.toUpperCase() : undefined,
      });
      if (user.status === "pending") {
        toast.success(`Bienvenido/a, ${user.name}. Tu cuenta quedó en espera de aprobación.`);
      } else {
        toast.success(`¡Bienvenido/a, ${user.name}!`);
      }
      onSuccess(user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "No se pudo crear la cuenta");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-fade-up" data-testid="student-auth">
      <button onClick={onBack} className="text-sm text-foreground/70 hover:text-primary mb-4 inline-flex items-center gap-1" data-testid="back-roles">
        <ChevronLeft className="w-4 h-4" /> Volver
      </button>
      <p className="text-xs tracking-[0.3em] uppercase font-bold text-primary mb-2 flex items-center gap-2">
        <GraduationCap className="w-3.5 h-3.5" /> Familia virreyista
      </p>
      <h1 className="font-display text-4xl text-primary mb-2">Soy estudiante</h1>
      <p className="text-sm text-foreground/70 mb-7">Crea tu cuenta con cualquier correo personal (gmail, hotmail, outlook, …) o ingresa si ya estás registrado.</p>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setError(""); }} className="w-full">
        <TabsList className="grid grid-cols-2 mb-6 bg-muted rounded-full p-1 h-12">
          <TabsTrigger value="login" className="rounded-full text-sm" data-testid="tab-login">Iniciar sesión</TabsTrigger>
          <TabsTrigger value="register" className="rounded-full text-sm" data-testid="tab-register">Crear cuenta</TabsTrigger>
        </TabsList>

        <TabsContent value="login">
          <form onSubmit={submitLogin} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="login-email" className="text-xs tracking-wider uppercase font-semibold">Correo electrónico</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/50" />
                <Input id="login-email" type="email" required value={loginForm.email}
                       onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                       className="pl-9 h-11" placeholder="tu_correo@ejemplo.com" data-testid="login-email" />
              </div>
            </div>
            <div>
              <Label htmlFor="login-password" className="text-xs tracking-wider uppercase font-semibold">Contraseña</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/50" />
                <Input id="login-password" type="password" required value={loginForm.password}
                       onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                       className="pl-9 h-11" placeholder="••••••••" data-testid="login-password" />
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={loginForm.remember} onCheckedChange={(v) => setLoginForm({ ...loginForm, remember: !!v })} data-testid="remember-checkbox" />
                <span className="text-foreground/70">Recordarme</span>
              </label>
              <button type="button" onClick={() => setForgotOpen(true)} className="text-primary hover:underline" data-testid="forgot-password">
                ¿Olvidaste tu contraseña?
              </button>
            </div>
            {error && <p className="text-sm text-destructive font-semibold" data-testid="stu-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-full font-semibold" data-testid="login-submit">
              {loading ? "Ingresando..." : "Iniciar sesión"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="register">
          <form onSubmit={submitRegister} className="space-y-4" data-testid="register-form">
            <div>
              <Label htmlFor="reg-name" className="text-xs tracking-wider uppercase font-semibold">Nombre completo</Label>
              <div className="relative mt-1.5">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/50" />
                <Input id="reg-name" required value={regForm.name}
                       onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                       className="pl-9 h-11" placeholder="María José Pérez" data-testid="reg-name" />
              </div>
            </div>
            <div>
              <Label htmlFor="reg-email" className="text-xs tracking-wider uppercase font-semibold">Correo electrónico</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/50" />
                <Input id="reg-email" type="email" required value={regForm.email}
                       onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                       className="pl-9 h-11" placeholder="tu_correo@ejemplo.com" data-testid="reg-email" />
              </div>
              <p className="text-xs text-foreground/60 mt-1">Se acepta cualquier correo válido (gmail, hotmail, outlook, yahoo, icloud…).</p>
            </div>
            <div>
              <Label htmlFor="reg-password" className="text-xs tracking-wider uppercase font-semibold">Contraseña</Label>
              <div className="relative mt-1.5">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/50" />
                <Input id="reg-password" type="password" required minLength={6} value={regForm.password}
                       onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                       className="pl-9 h-11" placeholder="mínimo 6 caracteres" data-testid="reg-password" />
              </div>
            </div>
            <div>
              <Label htmlFor="reg-group" className="text-xs tracking-wider uppercase font-semibold">Código de grupo (opcional)</Label>
              <div className="relative mt-1.5">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/50" />
                <Input id="reg-group" value={regForm.group_code}
                       onChange={(e) => setRegForm({ ...regForm, group_code: e.target.value.toUpperCase() })}
                       className="pl-9 h-11 uppercase" placeholder="DEMO-PC (opcional)" data-testid="reg-group" />
              </div>
              <p className="text-xs text-foreground/60 mt-1">Si tienes un código de tu catequista úsalo aquí, si no, el administrador te asignará el sacramento al aprobar tu cuenta.</p>
            </div>
            <div className="rounded-xl bg-secondary/10 border border-secondary/30 p-3">
              <p className="text-xs leading-snug text-foreground/80">
                <strong>Importante:</strong> tu cuenta quedará en <em>pendiente de aprobación</em>. Podrás iniciar sesión, pero el acceso a temas, quizzes y actividades estará habilitado tras la aprobación de la coordinación pastoral.
              </p>
            </div>
            {error && <p className="text-sm text-destructive font-semibold" data-testid="stu-error-reg">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-full font-semibold" data-testid="register-submit">
              {loading ? "Creando..." : "Crear cuenta"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
        </TabsContent>
      </Tabs>

      <ForgotPasswordDialog open={forgotOpen} onClose={() => setForgotOpen(false)} defaultEmail={loginForm.email} />
    </div>
  );
}

function ForgotPasswordDialog({ open, onClose, defaultEmail }) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [adminQuestion, setAdminQuestion] = useState(null); // null = not checked, false = none, string = question text
  const [answer, setAnswer] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [adminSuccess, setAdminSuccess] = useState(false);

  React.useEffect(() => {
    if (open) {
      setEmail(defaultEmail || ""); setResult(null);
      setAdminQuestion(null); setAnswer(""); setNewPwd(""); setConfirmPwd(""); setAdminSuccess(false);
    }
  }, [open, defaultEmail]);

  const submit = async (e) => {
    e.preventDefault();
    if (!isInstitutional(email)) { toast.error(`Usa tu correo institucional ${INST_DOMAIN}`); return; }
    setBusy(true);
    try {
      // Check if email belongs to an admin with security question
      const { data: q } = await api.get(`/auth/security-question`, { params: { email } });
      if (q?.available) {
        setAdminQuestion(q.question);
        return;
      }
      // Fallback: usual flow (manual reset coordinated by admin)
      const { data } = await api.post("/auth/forgot-password", { email });
      setResult(data);
    } catch (err) {
      toast.error("No se pudo procesar la solicitud");
    } finally { setBusy(false); }
  };

  const submitAdminReset = async (e) => {
    e.preventDefault();
    if (newPwd.length < 6) return toast.error("La contraseña debe tener al menos 6 caracteres");
    if (newPwd !== confirmPwd) return toast.error("Las contraseñas no coinciden");
    setBusy(true);
    try {
      await api.post("/auth/admin-self-reset", { email, answer, new_password: newPwd });
      setAdminSuccess(true);
      toast.success("Contraseña restablecida — ya puedes ingresar");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Respuesta incorrecta");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-primary">Recuperar contraseña</DialogTitle>
        </DialogHeader>
        {adminSuccess ? (
          <div className="space-y-4 text-center" data-testid="admin-reset-success">
            <div className="w-14 h-14 rounded-full bg-primary/10 text-primary mx-auto flex items-center justify-center">
              <ShieldCheck className="w-7 h-7" />
            </div>
            <p className="font-display text-xl text-primary">¡Contraseña actualizada!</p>
            <p className="text-sm text-foreground/70">Ingresa de nuevo con tu nueva contraseña.</p>
            <Button onClick={onClose} className="w-full rounded-full">Entendido</Button>
          </div>
        ) : adminQuestion ? (
          <form onSubmit={submitAdminReset} className="space-y-4" data-testid="admin-sq-form">
            <p className="text-sm text-foreground/70">
              Identificamos tu cuenta de administrador. Responde tu pregunta de seguridad para restablecer la contraseña.
            </p>
            <div className="rounded-xl bg-primary/5 border border-primary/30 p-4">
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-primary mb-1">Pregunta de seguridad</p>
              <p className="font-display text-lg text-primary" data-testid="admin-sq-question">{adminQuestion}</p>
            </div>
            <Input
              required
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Tu respuesta"
              data-testid="admin-sq-answer"
            />
            <Input
              required
              type="password"
              minLength={6}
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              placeholder="Nueva contraseña (mín. 6)"
              data-testid="admin-sq-newpwd"
            />
            <Input
              required
              type="password"
              minLength={6}
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              placeholder="Confirma la nueva contraseña"
              data-testid="admin-sq-confirm"
            />
            <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="admin-sq-submit">
              {busy ? "Restableciendo…" : "Restablecer contraseña"}
            </Button>
            <p className="text-xs text-foreground/60">
              ¿No recuerdas la respuesta? Actualízala en tu panel cuando vuelvas a ingresar.
            </p>
          </form>
        ) : !result ? (
          <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
            <p className="text-sm text-foreground/70">
              Si eres administrador, te pediremos tu pregunta de seguridad. Si eres catequista o estudiante,
              enviaremos tu solicitud a la coordinación pastoral.
            </p>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={`tu_nombre${INST_DOMAIN}`} data-testid="forgot-email" />
            <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="forgot-submit">
              {busy ? "Verificando…" : "Continuar"}
            </Button>
          </form>
        ) : (
          <div className="space-y-4 text-center" data-testid="forgot-result">
            <div className="w-14 h-14 rounded-full bg-primary/10 text-primary mx-auto flex items-center justify-center">
              <ShieldCheck className="w-7 h-7" />
            </div>
            <p className="font-display text-xl text-primary">Solicitud recibida</p>
            <p className="text-sm text-foreground/70">{result.message}</p>
            <p className="text-xs text-foreground/60">
              Comunícate con la coordinación pastoral del colegio para obtener tu enlace.
              Si nunca solicitaste esto, puedes ignorar este mensaje.
            </p>
            <Button onClick={onClose} variant="outline" className="w-full rounded-full">Entendido</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
